import { useState, useRef } from 'react';
import {
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Divider,
  IconButton,
} from '@mui/material';
import { CommandResultSnackbar } from '@/components/CommandResultSnackbar';
import Add from '@mui/icons-material/Add';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import MoreVert from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getTemplatesForEntity,
  resolveTemplatePayload,
  deleteTemplate,
  setTemplateDefault,
  clearTemplateDefault,
  type EntityTemplateListItem,
} from '@/services/core/entityTemplateApi';
import { getErrorMessage } from '@/lib/apiClient';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { pickDefaultTemplate } from './pickDefaultTemplate';

interface NewTemplateSplitButtonProps {
  /** Aggregate-root entity name, e.g. "Order". */
  entityName: string;
  /** Route to the create form, e.g. "/sales/orders/new". */
  createPath: string;
}

/**
 * "New" split button. The main button auto-applies the resolved scope default template
 * (spec §8/§9: personal default beats org-unit; none → blank form); the ▾ dropdown lists
 * in-scope templates and, on pick, resolves the payload and seeds the create form via
 * router state (`preFill`). Each row reveals a ⋮ menu (Edit / Delete) on hover.
 * Entity-name driven — never per-entity.
 */
export function NewTemplateSplitButton({ entityName, createPath }: NewTemplateSplitButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState<EntityTemplateListItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Per-row ⋮ actions menu + delete confirmation state.
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [actionsTemplate, setActionsTemplate] = useState<EntityTemplateListItem | null>(null);
  const [confirmTemplate, setConfirmTemplate] = useState<EntityTemplateListItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // The main "New" button auto-applies the scope default (if any) — `creating` covers that lookup.
  const [creating, setCreating] = useState(false);
  // Error surfaced when the per-row "Set/Remove default" toggle fails (no dialog to host it).
  const [toggleError, setToggleError] = useState<string | null>(null);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      setTemplates(await getTemplatesForEntity(entityName));
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const openMenu = async () => {
    setOpen(true);
    await loadTemplates();
  };

  const pick = async (id: string) => {
    setOpen(false);
    try {
      const preFill = await resolveTemplatePayload(id);
      navigate(createPath, { state: { preFill, templateId: id } });
    } catch {
      navigate(createPath);
    }
  };

  // Main "New": auto-apply the resolved scope default (spec §8/§9) — seed the create form from it
  // exactly like picking it. No default → blank form. Any failure falls back to a blank form so
  // "New" is never blocked.
  const createNew = async () => {
    setCreating(true);
    try {
      const def = pickDefaultTemplate(await getTemplatesForEntity(entityName));
      if (def) {
        const preFill = await resolveTemplatePayload(def.id);
        navigate(createPath, { state: { preFill, templateId: def.id } });
      } else {
        navigate(createPath);
      }
    } catch {
      navigate(createPath);
    } finally {
      setCreating(false);
    }
  };

  // Base path to the template editor, derived from createPath: "/sales/orders/new" → "/sales/orders/template/"
  const templateEditBase = createPath.replace(/\/new$/, '/template/');

  const openActions = (e: React.MouseEvent, tpl: EntityTemplateListItem) => {
    e.stopPropagation(); // don't trigger the row's "create from template"
    setActionsAnchor(e.currentTarget as HTMLElement);
    setActionsTemplate(tpl);
  };
  const closeActions = () => {
    setActionsAnchor(null);
    setActionsTemplate(null);
  };

  const editTemplate = () => {
    const id = actionsTemplate?.id;
    closeActions();
    setOpen(false);
    if (id) navigate(templateEditBase + id);
  };

  // Toggle this template as the scope default. Setting one clears the prior default at the same
  // scope (server-side), so reload afterwards to refresh the star + ordering for the whole list.
  const toggleDefault = async () => {
    const tpl = actionsTemplate;
    closeActions();
    if (!tpl) return;
    try {
      if (tpl.isDefault) {
        await clearTemplateDefault(tpl.id);
      } else {
        await setTemplateDefault(tpl.id);
      }
      await loadTemplates();
    } catch (e) {
      setToggleError(getErrorMessage(e));
    }
  };

  const requestDelete = () => {
    setDeleteError(null);
    setConfirmTemplate(actionsTemplate);
    closeActions();
  };

  const confirmDelete = async () => {
    if (!confirmTemplate) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteTemplate(confirmTemplate.id);
      setConfirmTemplate(null);
      await loadTemplates();
    } catch (e) {
      // Keep the dialog open on failure so the user sees why and can retry/cancel.
      setDeleteError(getErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <ButtonGroup
        ref={anchorRef}
        variant="outlined"
        color="primary"
        sx={{ '& .MuiButtonGroup-grouped': { textTransform: 'none', fontWeight: 500 } }}
      >
        <Button
          startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <Add />}
          onClick={createNew}
          disabled={creating}
          sx={{ px: 2 }}
        >
          {t('common.new')}
        </Button>
        <Button size="small" onClick={openMenu} aria-label={t('template.newFromTemplate')} disabled={creating}>
          <ArrowDropDown />
        </Button>
      </ButtonGroup>
      <Menu anchorEl={anchorRef.current} open={open} onClose={() => setOpen(false)}>
        <MenuItem disabled sx={{ opacity: 0.8, fontWeight: 600 }}>
          {t('template.newFromTemplate')}
        </MenuItem>
        <Divider />
        {loading && (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} /> {t('common.loading')}
          </MenuItem>
        )}
        {!loading && templates.length === 0 && <MenuItem disabled>{t('template.none')}</MenuItem>}
        {!loading &&
          templates.map((tpl) => (
            <MenuItem
              key={tpl.id}
              onClick={() => pick(tpl.id)}
              sx={{
                pr: 1,
                // ⋮ button is revealed only on row hover (or while its menu is open).
                '& .tpl-row-kebab': { visibility: actionsTemplate?.id === tpl.id ? 'visible' : 'hidden' },
                '&:hover .tpl-row-kebab': { visibility: 'visible' },
              }}
            >
              <ListItemIcon>
                <ContentCopy fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={tpl.name} secondary={tpl.description || undefined} />
              {tpl.isDefault && <Star fontSize="small" sx={{ ml: 1, color: 'warning.main' }} />}
              <IconButton
                className="tpl-row-kebab"
                size="small"
                edge="end"
                aria-label={t('template.actions')}
                onClick={(e) => openActions(e, tpl)}
                sx={{ ml: 1, color: 'text.secondary' }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </MenuItem>
          ))}
      </Menu>

      {/* Per-row actions (Set/Remove default · Edit · Delete) */}
      <Menu anchorEl={actionsAnchor} open={!!actionsAnchor} onClose={closeActions}>
        <MenuItem onClick={toggleDefault}>
          <ListItemIcon>
            {actionsTemplate?.isDefault ? (
              <StarBorder fontSize="small" />
            ) : (
              <Star fontSize="small" sx={{ color: 'warning.main' }} />
            )}
          </ListItemIcon>
          <ListItemText primary={actionsTemplate?.isDefault ? t('template.removeDefault') : t('template.setDefault')} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={editTemplate}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('common.edit')} />
        </MenuItem>
        <MenuItem onClick={requestDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText primary={t('common.delete')} />
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={!!confirmTemplate}
        title={t('template.deleteTitle')}
        message={
          deleteError
            ? t('template.deleteFailed', { error: deleteError })
            : confirmTemplate
              ? t('template.deleteConfirm', { name: confirmTemplate.name })
              : ''
        }
        confirmLabel={deleting ? t('template.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        confirmDisabled={deleting}
        onConfirm={confirmDelete}
        onCancel={() => !deleting && setConfirmTemplate(null)}
      />

      <CommandResultSnackbar
        result={toggleError ? { severity: 'error', message: toggleError } : null}
        onClose={() => setToggleError(null)}
      />
    </>
  );
}
